import { ref, computed } from 'vue';
import knowledgeBaseAPI from '../api/omni/knowledgeBase';
import { useAlert } from 'dashboard/composables';

export function useKnowledgeBase() {
  const entries = ref([]);
  const bots = ref([]);
  const loading = ref(false);
  const currentEntry = ref(null);
  const meta = ref({
    page: 1,
    per_page: 25,
    total: 0,
  });

  const totalPages = computed(() => {
    return Math.ceil(meta.value.total / meta.value.per_page);
  });

  const hasEntries = computed(() => entries.value.length > 0);

  const fetchEntries = async (params = {}) => {
    loading.value = true;
    try {
      const response = await knowledgeBaseAPI.get(params);
      entries.value = response.data.entries || [];
      meta.value = response.data.meta || meta.value;
    } catch (error) {
      useAlert(error.message || 'Error fetching knowledge base entries');
    } finally {
      loading.value = false;
    }
  };

  const fetchBots = async () => {
    try {
      const response = await knowledgeBaseAPI.getBots();
      bots.value = response.data || [];
    } catch (error) {
      useAlert(error.message || 'Error fetching bots');
    }
  };

  const fetchEntry = async (id) => {
    loading.value = true;
    try {
      const response = await knowledgeBaseAPI.show(id);
      currentEntry.value = response.data;
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error fetching entry');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const createEntry = async (entryData) => {
    loading.value = true;
    try {
      const response = await knowledgeBaseAPI.create(entryData);
      entries.value.unshift(response.data);
      meta.value.total += 1;
      useAlert('Entry created successfully', 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error creating entry');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const updateEntry = async (id, entryData) => {
    loading.value = true;
    try {
      const response = await knowledgeBaseAPI.update(id, entryData);
      const index = entries.value.findIndex(e => e.id === id);
      if (index !== -1) {
        entries.value[index] = response.data;
      }
      useAlert('Entry updated successfully', 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error updating entry');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const deleteEntry = async (id) => {
    loading.value = true;
    try {
      await knowledgeBaseAPI.delete(id);
      entries.value = entries.value.filter(e => e.id !== id);
      meta.value.total -= 1;
      useAlert('Entry deleted successfully', 'success');
    } catch (error) {
      useAlert(error.message || 'Error deleting entry');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const bulkCreate = async (entriesData) => {
    loading.value = true;
    try {
      const response = await knowledgeBaseAPI.bulkCreate(entriesData);
      entries.value = [...response.data.created, ...entries.value];
      meta.value.total += response.data.total_created;
      useAlert(`${response.data.total_created} entries created`, 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error bulk creating entries');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const changePage = async (page, additionalParams = {}) => {
    await fetchEntries({ page, ...additionalParams });
  };

  const regenerateEmbedding = async (id) => {
    try {
      await knowledgeBaseAPI.regenerateEmbedding(id);
      useAlert('Embedding generation queued', 'success');
    } catch (error) {
      useAlert(error.message || 'Error regenerating embedding');
      throw error;
    }
  };

  const regenerateAllEmbeddings = async (onlyMissing = false) => {
    try {
      const response = await knowledgeBaseAPI.regenerateAllEmbeddings(onlyMissing);
      useAlert(`Queued ${response.data.queued_count} entries for embedding`, 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error regenerating embeddings');
      throw error;
    }
  };

  return {
    entries,
    bots,
    loading,
    currentEntry,
    meta,
    totalPages,
    hasEntries,
    fetchEntries,
    fetchBots,
    fetchEntry,
    createEntry,
    updateEntry,
    deleteEntry,
    bulkCreate,
    changePage,
    regenerateEmbedding,
    regenerateAllEmbeddings,
  };
}

